import React, { useState, useEffect } from 'react';
import { X, Shield, Cookie, Settings } from '@/lib/icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useMarketingScripts } from '@/hooks/useMarketingScripts';

interface ConsentBannerProps {
  settings?: {
    consent_required: boolean;
    consent_message: string;
  };
}

export const ConsentBanner: React.FC<ConsentBannerProps> = ({ settings }) => {
  const { consentState, saveConsent } = useMarketingScripts();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Mostrar banner se:
    // 1. Consentimento é obrigatório
    // 2. Usuário ainda não deu consentimento
    // 3. Banner não foi fechado
    if (settings?.consent_required && !consentState.hasConsented) {
      setShowBanner(true);
    }
  }, [settings, consentState]);

  const handleAccept = () => {
    saveConsent(true);
    setShowBanner(false);
  };

  const handleReject = () => {
    saveConsent(false);
    setShowBanner(false);
  };

  const handleClose = () => {
    // Salvar como rejeitado se fechar sem escolher
    saveConsent(false);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4">
        <Card className="max-w-4xl mx-auto shadow-2xl border-2">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Privacidade e Cookies
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {settings?.consent_message || 'Este site utiliza cookies para melhorar sua experiência e analisar o tráfego.'}
                    </p>
                    
                    {!showDetails && (
                      <button
                        onClick={() => setShowDetails(true)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                      >
                        <Settings className="h-4 w-4" />
                        Configurar preferências
                      </button>
                    )}
                    
                    {showDetails && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3">Cookies utilizados:</h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Cookie className="h-4 w-4 text-green-600" />
                            <span className="text-gray-700">
                              <strong>Cookies Essenciais:</strong> Necessários para o funcionamento básico do site
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-blue-600" />
                            <span className="text-gray-700">
                              <strong>Cookies de Marketing:</strong> Para personalizar anúncios e medir campanhas
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          Você pode alterar suas preferências a qualquer momento nas configurações do seu navegador.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={handleClose}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Button
                    onClick={handleAccept}
                    className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700"
                  >
                    Aceitar Todos
                  </Button>
                  <Button
                    onClick={handleReject}
                    variant="outline"
                    className="flex-1 sm:flex-none"
                  >
                    Aceitar Apenas Essenciais
                  </Button>
                  {showDetails && (
                    <Button
                      onClick={() => setShowDetails(false)}
                      variant="ghost"
                      className="flex-1 sm:flex-none"
                    >
                      Fechar Detalhes
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
